import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

/**
 * Horizontal drag-and-drop for linked public slides in My Library only.
 * Updates per-user library order via API — does not change public slide order.
 */
function SubtitleReferencedSlidesDnD({
  subtitleId,
  slides,
  onReorder,
  renderSlide,
  droppableId,
}) {
  const theme = useTheme();

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const next = reorder(slides, result.source.index, result.destination.index);
    onReorder(subtitleId, next.map((s) => s.id));
  };

  if (!slides?.length) return null;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId} direction="horizontal">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start" }}
          >
            {slides.map((slide, index) => (
              <Draggable
                key={slide.id}
                draggableId={`${subtitleId}::${slide.id}`}
                index={index}
              >
                {(dragProvided, snapshot) => (
                  <Box
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    sx={{
                      position: "relative",
                      opacity: snapshot.isDragging ? 0.92 : 1,
                      transition: "box-shadow 0.15s ease, opacity 0.15s ease",
                      boxShadow: snapshot.isDragging ? theme.shadows[8] : undefined,
                      borderRadius: 3,
                    }}
                  >
                    <Box
                      {...dragProvided.dragHandleProps}
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        zIndex: 3,
                        cursor: "grab",
                        bgcolor: alpha(theme.palette.background.paper, 0.94),
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: 1,
                        "&:active": { cursor: "grabbing" },
                      }}
                      aria-label="Drag to reorder in your library"
                    >
                      <DragIndicatorIcon fontSize="small" color="action" />
                    </Box>
                    {renderSlide(slide)}
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default SubtitleReferencedSlidesDnD;
